import { Conversion } from '../crud-interfaces'
import { readMuConversions } from './metadata-storage'
import { DecimalInfo } from './number'

/**
 * Get the measurement unit label of the conversion if there is any
 */
export function getConversionUM(sourceUM: string, entityName: string, fieldName: string): string | undefined {
  const c = getConversion(sourceUM, entityName, fieldName)
  return c?.um
}

/**
 * Get the measurement unit label of the conversion or the source unit if no conversion is found
 */
export function tryGetConversionUM(sourceUM: string, entityName: string, fieldName: string): string {
  return getConversionUM(sourceUM, entityName, fieldName) ?? sourceUM
}

/**
 * If a conversion factor and offset are found for the given source unit of measurement,
 * entity name, and field name, the function will return the converted value calculated by multiplying
 * the input value by the conversion factor and adding the offset. If no conversion information is
 * found, the function will simply return the original input value.
 */
export function tryConvert(value: number, sourceUM: string, entityName: string, fieldName: string) {
  const c = getConversion(sourceUM, entityName, fieldName)
  if (c) {
    return value * c.factor + c.offset
  }
  return value
}

function calculatedMagnitude(conversionFactor: number) {
  if (conversionFactor === 0) return 0
  return Math.floor(Math.log10(Math.abs(conversionFactor)))
}

function GetPrecision(v: number): number {
  const str = v.toString()
  const dotIndex = str.indexOf('.')
  return dotIndex === -1 ? 0 : str.length - dotIndex - 1
}

function convertDecimalInfo(
  decimalInfo: DecimalInfo | undefined,
  factor: number,
  step: number | undefined,
): DecimalInfo | undefined {
  if (!decimalInfo) {
    return undefined
  }

  const convertedStep = step !== undefined ? step * factor : undefined
  const stepMagnitude = convertedStep !== undefined ? -GetPrecision(convertedStep) : undefined
  const factorMagnitude = calculatedMagnitude(factor)
  const magnitude = Math.min(stepMagnitude ?? Number.MAX_VALUE, factorMagnitude)

  if (magnitude === 0) {
    return decimalInfo
  }

  let requiredDecimal = decimalInfo.requiredDecimal ?? 0
  const decimalToAdd = -magnitude

  requiredDecimal = (decimalInfo.requiredDecimal ?? 0) + decimalToAdd
  const optionalDecimal = decimalInfo.optionalDecimal ?? 0
  const decimalLimit = requiredDecimal + optionalDecimal

  return {
    decimalLimit: decimalLimit > 0 ? decimalLimit : 0,
    requiredDecimal: requiredDecimal > 0 ? requiredDecimal : 0,
    optionalDecimal: optionalDecimal > 0 ? optionalDecimal : 0,
  }
}

export function tryConvertDecimalInfo(
  decimalInfo: DecimalInfo | undefined,
  sourceUM: string,
  entityName: string,
  fieldName: string,
  step: number | undefined = undefined,
): DecimalInfo | undefined {
  if (!decimalInfo) {
    return undefined
  }

  const c = getConversion(sourceUM, entityName, fieldName)

  if (c) {
    return convertDecimalInfo(decimalInfo, c.factor, step)
  }
  return decimalInfo
}

/**
 * If the `getConversion` function returns a valid conversion object `c`, the function
 * `tryRevert` will return the result of the expression `(value - c.offset) / c.factor`. Otherwise, it
 * will return the original `value` without any modification.
 */
export function tryRevert(value: number, sourceUM: string, entityName: string, fieldName: string) {
  const c = getConversion(sourceUM, entityName, fieldName)
  if (c) {
    return (value - c.offset) / c.factor
  }
  return value
}

/**
 * get the conversion for the field if it exists
 */
function getConversion(sourceUM: string, entityName: string, fieldName: string): Conversion | undefined {
  const conversions = readMuConversions()
  let c = conversions[`${sourceUM}.${entityName}.${fieldName}`]
  if (!c) c = conversions[`${sourceUM}.${entityName}`]
  if (!c) c = conversions[`${sourceUM}`]

  return c
}
